import { configureStore } from "@reduxjs/toolkit"
import { setupListeners } from "@reduxjs/toolkit/query"

import { apiKeysApi } from "@/api/routes/api-keys"
import { billingApi } from "@/api/routes/billing"
import { buildsApi } from "@/api/routes/builds"
import { dnsApi } from "@/api/routes/dns"
import { cloudEndpointsApi } from "@/api/routes/endpoints"
import { irohNodesApi } from "@/api/routes/iroh-nodes"
import { metricsApi } from "@/api/routes/metrics"

import { projectActivityApi } from "@/api/routes/project-activity"
import { projectInvitesApi } from "@/api/routes/project-invites"
import { projectsApi } from "@/api/routes/projects"
import { relaysApi } from "@/api/routes/relays"
import { staffApi } from "@/api/routes/staff"
import { usersApi } from "@/api/routes/users"
import {
  AlertReducer,
  type AlertsAppState,
} from "@/components/alerts/alertModel"
import {
  type SlideOverAppState,
  SlideOverReducer,
} from "@/components/slideOver/slideOvers"
import { useDispatch, useSelector, useStore } from "react-redux"
import { netDiagnosticsApi } from "./api/routes/net-diagnostics"
import { workflowsApi } from "./api/routes/workflows"

export interface AppState {
  [apiKeysApi.reducerPath]: ReturnType<typeof apiKeysApi.reducer>
  [billingApi.reducerPath]: ReturnType<typeof billingApi.reducer>
  [buildsApi.reducerPath]: ReturnType<typeof buildsApi.reducer>
  [cloudEndpointsApi.reducerPath]: ReturnType<typeof cloudEndpointsApi.reducer>
  [dnsApi.reducerPath]: ReturnType<typeof dnsApi.reducer>
  [irohNodesApi.reducerPath]: ReturnType<typeof irohNodesApi.reducer>
  [metricsApi.reducerPath]: ReturnType<typeof metricsApi.reducer>
  [netDiagnosticsApi.reducerPath]: ReturnType<typeof netDiagnosticsApi.reducer>
  [projectActivityApi.reducerPath]: ReturnType<
    typeof projectActivityApi.reducer
  >
  [projectInvitesApi.reducerPath]: ReturnType<typeof projectInvitesApi.reducer>
  [projectsApi.reducerPath]: ReturnType<typeof projectsApi.reducer>
  [relaysApi.reducerPath]: ReturnType<typeof relaysApi.reducer>
  [staffApi.reducerPath]: ReturnType<typeof staffApi.reducer>
  [usersApi.reducerPath]: ReturnType<typeof usersApi.reducer>
  [workflowsApi.reducerPath]: ReturnType<typeof workflowsApi.reducer>

  slideOver: SlideOverAppState
  alerts: AlertsAppState
}

// const logger = (_: any) => (next: any) => (action: any) => {
// 	console.log("dispatching", action);
// 	// let result = next(action)
// 	// console.log('next state', store.getState())
// 	return next(action);
// };

export const store = configureStore({
  reducer: {
    // Add the generated reducer as a specific top-level slice
    [apiKeysApi.reducerPath]: apiKeysApi.reducer,
    [billingApi.reducerPath]: billingApi.reducer,
    [buildsApi.reducerPath]: buildsApi.reducer,
    [cloudEndpointsApi.reducerPath]: cloudEndpointsApi.reducer,
    [dnsApi.reducerPath]: dnsApi.reducer,
    [irohNodesApi.reducerPath]: irohNodesApi.reducer,
    [metricsApi.reducerPath]: metricsApi.reducer,
    [netDiagnosticsApi.reducerPath]: netDiagnosticsApi.reducer,
    [projectActivityApi.reducerPath]: projectActivityApi.reducer,
    [projectInvitesApi.reducerPath]: projectInvitesApi.reducer,
    [projectsApi.reducerPath]: projectsApi.reducer,
    [relaysApi.reducerPath]: relaysApi.reducer,
    [staffApi.reducerPath]: staffApi.reducer,
    [usersApi.reducerPath]: usersApi.reducer,
    [workflowsApi.reducerPath]: workflowsApi.reducer,

    slideOver: SlideOverReducer,
    alerts: AlertReducer,
  },
  // Adding the api middleware enables caching, invalidation, polling,
  // and other useful features of `rtk-query`.
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(
      apiKeysApi.middleware,
      billingApi.middleware,
      buildsApi.middleware,
      cloudEndpointsApi.middleware,
      dnsApi.middleware,
      irohNodesApi.middleware,
      workflowsApi.middleware,
      metricsApi.middleware,
      netDiagnosticsApi.middleware,
      relaysApi.middleware,
      staffApi.middleware,
      projectsApi.middleware,
      projectActivityApi.middleware,
      projectInvitesApi.middleware,
      usersApi.middleware,
      // logger,
    ),
})

// optional, but required for refetchOnFocus/refetchOnReconnect behaviors
// see `setupListeners` docs - takes an optional callback as the 2nd arg for customization
setupListeners(store.dispatch)

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch
export type AppStore = typeof store

export const useAppDispatch = useDispatch.withTypes<AppDispatch>()
export const useAppSelector = useSelector.withTypes<RootState>()
export const useAppStore = useStore.withTypes<AppStore>()
